import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { originalAccountTypes, syncAccountTypesFromConfig } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  copyAccountTypeProfile,
  deleteAccountTypeProfile,
  getAccountTypeUsage,
  getAdminConfig,
  setAdminConfig,
} from 'services'
import { Checkbox, Tooltip } from 'stories/components'
import { confirm, prompt } from 'utils'

interface IAccountTypesConfig {
  title: string
  visible: boolean
  baseType?: string
}

export const ManageAccountTypes = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Record<string, IAccountTypesConfig>>({})
  const [usage, setUsage] = useState<Record<string, number>>({})
  const [isChanged, setChanged] = useState(false)

  const loadUsage = () =>
    getAccountTypeUsage()
      .then(setUsage)
      .catch(() => setUsage({}))

  const loadData = () => {
    setLoading(true)
    return getAdminConfig('accountTypes')
      .then((config) => {
        setData(config)
        syncAccountTypesFromConfig(config)
        return loadUsage()
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadData()
  }, [])

  const onSubmit = async () => {
    setLoading(true)
    setAdminConfig('accountTypes', data)
      .then(() => {
        syncAccountTypesFromConfig(data)
        setChanged(false)
        toast(`Account Types Configuration has been saved.`, { type: 'info' })
      })
      .finally(() => setLoading(false))
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data) as any
    if (temp[key] === undefined) temp[key] = {}
    temp[key][map] = value
    setData(temp)
    setChanged(true)
  }

  const onCopyProfile = async (sourceKey: string) => {
    const title = await prompt('Enter a name for the new account type profile:', { required: true })
    if (!title?.trim()) return

    setLoading(true)
    try {
      await copyAccountTypeProfile(sourceKey, title.trim())
      toast(`Profile "${title.trim()}" has been created.`, { type: 'info' })
      setChanged(false)
      await loadData()
    } catch (e: any) {
      toast(e?.message || 'Failed to copy profile.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onDeleteProfile = async (key: string) => {
    const title = data[key]?.title || key
    const ok = await confirm(`Delete profile "${title}"? This cannot be undone.`)
    if (!ok) return

    setLoading(true)
    try {
      await deleteAccountTypeProfile(key)
      toast(`Profile "${title}" has been deleted.`, { type: 'info' })
      setChanged(false)
      await loadData()
    } catch (e: any) {
      toast(e?.message || 'Failed to delete profile.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const originalLabel = (key: string, config: IAccountTypesConfig) => {
    if (originalAccountTypes[key]) return originalAccountTypes[key]
    const parentKey = config.baseType || ''
    const parentTitle = originalAccountTypes[parentKey] || data[parentKey]?.title || parentKey
    return parentTitle ? `Copy of ${parentTitle}` : key
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />
      <div className="mt-4">
        <table className="text-sm">
          <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
            <tr>
              {['No', 'Original', 'New', 'In Use', 'Actions'].map((v) => (
                <th scope="col" className="p-3 border" key={`header-${v}`}>
                  {v}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data).map((key, index) => {
              const config = data[key]
              const { title, visible } = config
              const isBuiltIn = !!originalAccountTypes[key]
              const canDelete = !isBuiltIn && (usage[key] ?? 0) === 0
              return (
                <tr key={index}>
                  <td className="p-2 border">{index + 1}</td>
                  <td className="p-2 border">{originalLabel(key, config)}</td>
                  <td className="px-3 border">
                    <PlainInput
                      value={title}
                      content={title}
                      onChange={(value: string) => onChange(key, value, 'title')}
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <Checkbox
                      color="gray"
                      id={key}
                      title={''}
                      key={key}
                      value={visible}
                      onChange={(value) => onChange(key, value, 'visible')}
                      className="w-[none]"
                    />
                  </td>
                  <td className="px-3 border text-center">
                    <div className="inline-flex items-center justify-center gap-1">
                      {isBuiltIn && key !== 'admin' && (
                        <Tooltip message="Copy Profile">
                          <span className="btn-icon inline-flex" onClick={() => onCopyProfile(key)}>
                            <DocumentDuplicateIcon className="w-4 h-4 text-gray-500" />
                          </span>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip message="Delete Profile">
                          <span className="btn-icon inline-flex" onClick={() => onDeleteProfile(key)}>
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
